import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material';
import { Subscription } from 'rxjs';
import { AppService } from 'src/@core/services/app.service';
import { JobService } from 'src/@core/services/job/job.service';
import { NotificationService } from 'src/@core/services/notification.service';
import { openPresignedDownload } from 'src/@core/utils/presigned-download.util';
import { ClientProfileComponent } from '../../view-project-new/client-profile/client-profile.component';
import { EntityLeadsFacadeService } from '../services/entity-leads-facade.service';
import {
  buildCallHistoryFilters,
  buildPageNumbers,
  canNextPage,
  canPrevPage,
  getPaginationRange,
} from '../utils/entity-leads.utils';

@Component({
  selector: 'hour4u-admin-entity-outcomes-page',
  templateUrl: './entity-outcomes-page.component.html',
  styleUrls: ['./entity-outcomes-page.component.scss']
})
export class EntityOutcomesPageComponent implements OnInit, OnDestroy {
  profile = this.appService.getCurrentAccount();

  projectsList: any[] = [];
  selectedProjectId: string | null = null;
  projectDetails: any = null;

  outcomeOptions: string[] = [];
  selectedOutcomes: string[] = [];
  searchQuery = '';

  leadsStatusCount: any = null;
  isExporting = false;

  outcomeRows: any[] = [];
  isLoading = false;
  isLoadingProjects = false;
  isLoadingOutcomes = false;

  pageIndex = 1;
  pageSize = 10;
  pagination: any = null;
  pageNumbers: Array<number | string> = [];
  currentRange = '';

  private searchTimeout: any;
  private subs = new Subscription();

  constructor(
    private appService: AppService,
    private facade: EntityLeadsFacadeService,
    private dialog: MatDialog,
    private cd: ChangeDetectorRef,
    private jobService: JobService,
    private notify: NotificationService,
  ) { }

  ngOnInit() {
    const entityId = this.getEntityId();
    if (!entityId) {
      return;
    }

    this.subs.add(this.facade.loadingOutcomes$.subscribe((loading) => {
      this.isLoadingOutcomes = loading;
      this.cd.markForCheck();
    }));

    this.subs.add(this.facade.loadingTable$.subscribe((loading) => {
      this.isLoading = loading;
      this.cd.markForCheck();
    }));

    this.subs.add(this.facade.outcomeOptions$.subscribe((options) => {
      this.outcomeOptions = options;
      this.cd.markForCheck();
    }));

    this.subs.add(this.facade.tableResult$.subscribe((result) => {
      this.outcomeRows = result.rows;
      this.pagination = result.pagination;
      if (this.pagination) {
        this.pageNumbers = buildPageNumbers(this.pagination);
        this.currentRange = getPaginationRange(this.pagination);
      } else {
        this.pageNumbers = [];
        this.currentRange = '0 - 0 of 0 records';
      }
      this.cd.detectChanges();
    }));

    this.refreshPageData();
  }

  ngOnDestroy() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    this.subs.unsubscribe();
  }

  onProjectsLoaded(projects: any[]) {
    this.projectsList = projects;
    // Only a single project can be selected on this page (no "All projects"),
    // so default to the first available project when none is selected yet.
    if (!this.selectedProjectId && projects && projects.length) {
      this.onProjectChange(projects[0].project_id);
    }
    this.cd.markForCheck();
  }

  onProjectChange(projectId: string | null) {
    this.selectedProjectId = projectId || null;
    this.selectedOutcomes = [];
    this.pageIndex = 1;
    this.projectDetails = null;
    this.leadsStatusCount = null;

    const entityId = this.getEntityId();
    if (entityId && this.selectedProjectId) {
      this.facade.getProjectDetails(entityId, this.selectedProjectId).subscribe((res) => {
        this.projectDetails = res;
        this.cd.markForCheck();
      });
    }

    this.fetchLeadsStatusCount();
    this.refreshPageData();
  }

  private fetchLeadsStatusCount() {
    if (!this.selectedProjectId) {
      this.leadsStatusCount = null;
      return;
    }
    this.jobService.fetchLeadsStatusCount(this.selectedProjectId).subscribe(
      (res: any) => {
        this.leadsStatusCount = res;
        this.cd.markForCheck();
      },
      () => {
        this.leadsStatusCount = null;
      }
    );
  }

  getOutcomeCount(outcome: string): number {
    const counts = this.leadsStatusCount && this.leadsStatusCount.status_counts;
    if (!counts || !outcome) {
      return 0;
    }
    if (counts[outcome] != null) {
      return Number(counts[outcome]) || 0;
    }
    // Fall back to a case-insensitive match when keys don't line up exactly.
    const target = outcome.trim().toLowerCase();
    const key = Object.keys(counts).find((k) => k.trim().toLowerCase() === target);
    return key ? (Number(counts[key]) || 0) : 0;
  }

  exportOutcomes() {
    const projectId = this.selectedProjectId;
    if (!projectId) {
      this.notify.showMessage('Select a project to export.', 3000);
      return;
    }
    if (this.isExporting) {
      return;
    }

    this.isExporting = true;
    this.notify.showMessage('Preparing your export… this can take up to a couple of minutes for large projects.', 4000);

    this.jobService.exportConnectedLeadsExcel(
      projectId,
      this.selectedOutcomes.length ? this.selectedOutcomes : undefined
    ).subscribe(
      (result) => {
        this.isExporting = false;
        openPresignedDownload(result.downloadUrl, result.filename);
        this.cd.markForCheck();
      },
      (err) => {
        this.isExporting = false;
        this.notify.showMessage(this.getExportErrorMessage(err), 5000);
        this.cd.markForCheck();
      }
    );
  }

  private getExportErrorMessage(err: any): string {
    if (err === 'EXPORT_TIMEOUT') {
      return 'Export is taking longer than expected. Please try again.';
    }
    return typeof err === 'string' ? err : 'Export failed. Please try again.';
  }

  onSearchChange(value: string) {
    this.searchQuery = value;
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    this.searchTimeout = setTimeout(() => {
      this.pageIndex = 1;
      this.loadTable();
    }, 500);
  }

  toggleOutcomeSelection(outcome: string) {
    const index = this.selectedOutcomes.indexOf(outcome);
    if (index >= 0) {
      this.selectedOutcomes = this.selectedOutcomes.filter((o) => o !== outcome);
    } else {
      this.selectedOutcomes = [...this.selectedOutcomes, outcome];
    }
    this.pageIndex = 1;
    this.loadTable();
  }

  isOutcomeSelected(outcome: string): boolean {
    return this.selectedOutcomes.includes(outcome);
  }

  isAllProjectsSelected(): boolean {
    return !this.selectedProjectId;
  }

  get totalConversations(): number {
    if (this.pagination && this.pagination.totalElements != null) {
      return Number(this.pagination.totalElements) || 0;
    }
    return this.outcomeRows.length;
  }

  onNext() {
    if (!this.canNext() || this.isLoading) {
      return;
    }
    this.pageIndex = this.getPageNum() + 1;
    this.loadTable();
  }

  onPrev() {
    if (!this.canPrev() || this.isLoading) {
      return;
    }
    this.pageIndex = this.getPageNum() - 1;
    this.loadTable();
  }

  onPageSelect(page: number | string) {
    if (!this.pagination || typeof page !== 'number' || page === this.getPageNum() || this.isLoading) {
      return;
    }
    this.pageIndex = page;
    this.loadTable();
  }

  canPrev(): boolean {
    return canPrevPage(this.pagination);
  }

  canNext(): boolean {
    return canNextPage(this.pagination);
  }

  getPageNum(): number {
    if (this.pagination && this.pagination.currentPageNum != null) {
      return Number(this.pagination.currentPageNum) || 1;
    }
    return this.pageIndex || 1;
  }

  isEllipsis(page: number | string): boolean {
    return page === '...';
  }

  trackByCallId(index: number, call: any): any {
    return (call && call.call_sid) || (call && call.people_id) || index;
  }

  trackByPageNumber(index: number): number {
    return index;
  }

  viewClientProfile(call: any, event?: Event) {
    if (event && event.target) {
      const target = event.target as HTMLElement;
      if (target.closest('.row-action')) {
        return;
      }
    }
    const projectId = call.project_id
      || (this.projectDetails && this.projectDetails.project_id)
      || (this.isAllProjectsSelected() ? null : this.selectedProjectId);
    this.dialog.open(ClientProfileComponent, {
      disableClose: true,
      width: '45%',
      height: '100vh',
      position: { right: '0', top: '0', bottom: '0' },
      panelClass: 'add-edit-role-dialog',
      data: {
        projectDetails: this.projectDetails,
        projectId,
        callDetails: {
          people_id: call.people_id,
          project_id: call.project_id || projectId,
          client_number: call.client_number,
          client_name: call.client_name,
          client_email: call.client_email,
          whatsapp_chat_history: call.whatsapp_chat_history
        },
        selectedTabIndex: 1
      },
      autoFocus: true
    });
  }

  hasLeadStage(call: any): boolean {
    return !!(call && (call.lead_stage || call.engagement_status || call.funnel_stage));
  }

  getLeadStageLabel(call: any): string {
    const stage = call && (call.lead_stage || call.engagement_status || call.funnel_stage);
    return stage ? String(stage).toUpperCase() : '';
  }

  getLeadStageClass(): string {
    return 'lead-stage lead-stage--default';
  }

  hasLeadScore(call: any): boolean {
    return this.getLeadScorePercent(call) != null;
  }

  getLeadScorePercent(call: any): number | null {
    if (!call) return null;
    if (call.total_score != null && call.total_score !== 'Calculating Score') {
      const total = Number(call.total_score);
      if (!isNaN(total)) return total <= 1 ? Math.round(total * 100) : Math.round(total);
    }
    if (call.proficiency_score && call.proficiency_score.overall_score != null) {
      const score = Number(call.proficiency_score.overall_score);
      if (!isNaN(score)) return score <= 10 ? Math.round(score * 10) : Math.round(score);
    }
    return null;
  }

  getScoreDotCount(call: any): number {
    const percent = this.getLeadScorePercent(call);
    return percent == null ? 0 : Math.max(0, Math.min(5, Math.round(percent / 20)));
  }

  formatOutcomeDateTime(call: any): string {
    const raw = call && (call._raw_start_time || call.start_time);
    if (!raw) return '—';
    const timestamp = typeof raw === 'string' ? parseFloat(raw) : raw;
    if (!timestamp || isNaN(timestamp)) return '—';
    const date = new Date(timestamp * 1000);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const period = hours >= 12 ? 'pm' : 'am';
    const pad = (num: number) => (num < 10 ? `0${num}` : `${num}`);
    return `${pad(date.getDate())} ${months[date.getMonth()]}, ${hours % 12 || 12}:${pad(minutes)} ${period}`;
  }

  formatPhoneDisplay(call: any): string {
    return (call && call.client_number) || '—';
  }

  getProjectNameForCall(call: any): string {
    if (call && call.project_name) {
      return call.project_name;
    }
    const projectId = call && call.project_id;
    if (projectId) {
      const project = this.projectsList.find((p) => p.project_id === projectId);
      if (project) {
        return project.title || project.project_name || '—';
      }
    }
    return '—';
  }

  getOutcomeDisplayLabel(call: any): string {
    return (call && call.status) ? String(call.status) : '—';
  }

  getAssignedLabel(call: any): string {
    const assigned = call && (call.assigned_to || call.assigned_user);
    return assigned ? String(assigned) : '—';
  }

  private refreshPageData() {
    const entityId = this.getEntityId();
    if (!entityId) {
      return;
    }
    const projectId = this.selectedProjectId || undefined;
    this.facade.loadOutcomeOptions(entityId, projectId);
    this.loadTable();
  }

  private loadTable() {
    const entityId = this.getEntityId();
    if (!entityId) {
      return;
    }
    this.facade.loadTable({
      entityId,
      projectId: this.selectedProjectId || undefined,
      pageSize: this.pageSize,
      pageIndex: this.pageIndex,
      filters: buildCallHistoryFilters({
        searchQuery: this.searchQuery,
        selectedOutcomes: this.selectedOutcomes,
        isOutcomesMode: true,
      }),
      formatStartTime: false,
    });
  }

  getEntityId(): string | undefined {
    return this.profile && this.profile.entity && this.profile.entity.id;
  }
}
